import * as React from "react";
import { Mail, UserRoundCheck } from "lucide-react";
import { Avatar } from "../../../Avatar";
import {
  cardDeck,
  cardDeckClickable,
  cardStack,
  cardStackClickable,
} from "../ListViewCard/ListViewCard.style.css";
import type { ListViewCardLayout } from "../ListViewCard";
import * as s from "./ListViewTechnicianCard.style.css";
import { useT } from "../../../../../app/lib/i18n";
import {
  CertificationModulesCircularProgress,
  mergeCertificationModuleCircles,
  type CertificationModuleCatalogItem,
  type CertificationModuleCircleItem,
} from "../../../CertificationModulesCircularProgress";

export interface ListViewTechnicianCardModuleProgress {
  moduleId: string;
  moduleTitle: string;
  progressPercent: number;
  status: "not_started" | "in_progress" | "retake_needed" | "complete" | string;
  completedAt?: string | null;
  expiresAt?: string | null;
  certificateNumber?: string | null;
  trophyImageUrl?: string | null;
}

export interface ListViewTechnicianCardTechnician {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  lastLoginAt?: string | null;
  modules: ListViewTechnicianCardModuleProgress[];
}

export type ListViewTechnicianCardBadgeVariant = "invited" | "access_requested";

export interface ListViewTechnicianCardProps {
  layout: ListViewCardLayout;
  technician: ListViewTechnicianCardTechnician;
  onClick?: () => void;
  /**
   * Full module catalog. When provided, every module is shown as a circular
   * progress indicator (including not-started). When omitted, only the
   * technician's enrolled modules are shown.
   */
  catalogModules?: CertificationModuleCatalogItem[];
  /** @deprecated Vertical list scrolling — circular row scrolls horizontally. */
  moduleListMaxHeight?: number;
  /** Optional status chip in the header (e.g. invite pending). */
  badge?: { label: string; variant?: ListViewTechnicianCardBadgeVariant };
  /** Override empty-modules copy (default: not enrolled). */
  emptyModulesLabel?: string;
  /** Shows a last sign-in line under the email when true. */
  showLastSignIn?: boolean;
  /** Enables the download button in completed modules' hover cards. */
  onDownloadCertificate?: (moduleId: string) => void;
}

const BADGE_CLASS: Record<ListViewTechnicianCardBadgeVariant, string> = {
  invited: s.statusInvited,
  access_requested: s.statusAccessRequested,
};

function toCircleItems(
  modules: ListViewTechnicianCardModuleProgress[],
): CertificationModuleCircleItem[] {
  return modules.map((m) => ({
    moduleId: m.moduleId,
    moduleTitle: m.moduleTitle,
    progressPercent: m.progressPercent,
    status: m.status,
    completedAt: m.completedAt,
    expiresAt: m.expiresAt,
    certificateNumber: m.certificateNumber,
    trophyImageUrl: m.trophyImageUrl,
  }));
}

function formatLastSignIn(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ListViewTechnicianCardImpl({
  layout,
  technician,
  onClick,
  catalogModules,
  badge,
  emptyModulesLabel,
  showLastSignIn,
  onDownloadCertificate,
}: ListViewTechnicianCardProps) {
  const t = useT();
  const [firstName, ...rest] = technician.name.split(" ");
  const lastName = rest.join(" ");
  const isClickable = Boolean(onClick);

  const outerClass =
    layout === "deck"
      ? [cardDeck, isClickable ? cardDeckClickable : "", s.cardOverflowVisible]
          .filter(Boolean)
          .join(" ")
      : [cardStack, isClickable ? cardStackClickable : "", s.cardOverflowVisible]
          .filter(Boolean)
          .join(" ");

  const lastSignInLabel = technician.lastLoginAt
    ? formatLastSignIn(technician.lastLoginAt)
    : null;

  const badgeClass =
    BADGE_CLASS[badge?.variant ?? "invited"] ?? s.statusInvited;

  const progressItems = toCircleItems(technician.modules);
  const displayModules =
    catalogModules && catalogModules.length > 0
      ? mergeCertificationModuleCircles(catalogModules, progressItems)
      : progressItems;

  // Invites / access requests keep the empty label; enrolled users show circles
  // for the full catalog (including not-started modules).
  const showEmpty =
    displayModules.length === 0 ||
    (Boolean(badge) && technician.modules.length === 0);

  const emptyIcon =
    badge?.variant === "access_requested" ? (
      <UserRoundCheck size={28} strokeWidth={1.75} aria-hidden />
    ) : (
      <Mail size={28} strokeWidth={1.75} aria-hidden />
    );

  return (
    <div
      className={outerClass}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{ cursor: isClickable ? "pointer" : undefined, minHeight: 0 }}
    >
      <div className={s.cardInner}>
        <div className={s.cardHeader}>
          <Avatar
            email={technician.email}
            firstName={firstName ?? ""}
            lastName={lastName}
            image={technician.avatarUrl ?? undefined}
            size={40}
          />
          <div className={s.cardHeaderText}>
            <div className={s.headerNameRow}>
              <span className={s.headerName}>{technician.name}</span>
              {badge ? <span className={badgeClass}>{badge.label}</span> : null}
            </div>
            <span className={s.headerEmail}>{technician.email}</span>
            {showLastSignIn ? (
              <span className={s.headerLastSignIn}>
                {lastSignInLabel
                  ? t("pro.system.certification.technicians.lastSignIn", { date: lastSignInLabel })
                  : t("pro.system.certification.technicians.neverSignedIn")}
              </span>
            ) : null}
          </div>
        </div>

        {showEmpty ? (
          <div className={s.emptyModulesPanel}>
            <p className={s.emptyModules}>
              {emptyModulesLabel ?? t("shared.listView.notEnrolledModules")}
            </p>
            <span className={s.emptyModulesIcon}>{emptyIcon}</span>
          </div>
        ) : (
          <div className={s.moduleCircles}>
            <CertificationModulesCircularProgress
              modules={displayModules}
              onDownloadCertificate={onDownloadCertificate}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export const ListViewTechnicianCard = ListViewTechnicianCardImpl;
